import { Icon, Typography } from '@/components/common';
import { Option } from '@/interfaces';
import clsx from 'clsx';
import Link from 'next/link';
import { FC } from 'react';
import { useRouter } from 'next/router';

interface SecondSidebarProps {
	title: string | React.ReactNode;
	sections: Option[];
	linkClassName?: string;
	onlyMobile?: boolean;
}

export const SecondSidebarDesktop: FC<SecondSidebarProps> = ({
	title,
	sections,
	linkClassName,
	onlyMobile,
}) => {
	const router = useRouter();
	const asPath = router.asPath;
	return (
		<>
			{!onlyMobile && (
				<div className="hidden lg:block bg-grey-2 border-r border-gray-disabled px-4 py-6 h-screen fixed left-[105px] top-[58px] z-20 w-[192px]">
					<Typography type="semibold-14" className="text-opacity-black mb-6">
						{title}
					</Typography>
					<ul className="space-y-2">
						{sections
							.filter((section) => !section.isMobile)
							.map((section) => {
								// Matching logic:
								// 1. If the href has a query param, it's a specific sub-page (like Analytics tabs), use strict match.
								// 2. If it's a general section, ignore asPath query params to stay highlighted while filtering/searching.
								// 3. To avoid double highlighting (e.g. /marketing matching /marketing/client-targeting),
								// we prefer the most specific (longest) matching href.

								const currentPath = asPath.split('?')[0];

								const isBestMatch =
									sections
										.filter((s) => s.href && !s.isMobile)
										.map((s) => {
											const sHref = s.href as string;
											const sHasQuery = sHref.includes('?');
											const sPath = sHref.split('?')[0];
											const isMatch = sHasQuery
												? asPath === sHref
												: currentPath === sPath ||
												  currentPath.startsWith(sPath + '/');
											return { s, isMatch, length: sHref.length };
										})
										.filter((m) => m.isMatch)
										.sort((a, b) => b.length - a.length)[0]?.s === section;

								const isActive =
									isBestMatch || section.className?.includes('bg-bg-primary');

								return (
									<li key={section.name}>
										<Link
											href={section.href ?? '#'}
											className={clsx(
												'flex items-center gap-x-2 p-3 rounded-full',
												{
													'bg-bg-primary': isActive,
													'pointer-events-none': section.disabled,
												},
												section.className,
												linkClassName
											)}
										>
											<Icon
												src={section.icon || ''}
												width={20}
												height={20}
												className="!text-opacity-black"
											/>
											<Typography
												type="semibold-12"
												className="text-opacity-black"
											>
												{section.label}
											</Typography>
										</Link>
									</li>
								);
							})}
					</ul>
				</div>
			)}

			<div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-disabled py-2 z-30">
				<ul className="flex items-center justify-around gap-x-3">
					{sections.map((section) => {
						// Mobile matching logic (same as desktop)
						const currentPath = asPath.split('?')[0];
						const isBestMatch =
							sections
								.filter((s) => s.href)
								.map((s) => {
									const sHref = s.href as string;
									const sHasQuery = sHref.includes('?');
									const sPath = sHref.split('?')[0];
									const isMatch = sHasQuery
										? asPath === sHref
										: currentPath === sPath ||
										  currentPath.startsWith(sPath + '/');
									return { s, isMatch, length: sHref.length };
								})
								.filter((m) => m.isMatch)
								.sort((a, b) => b.length - a.length)[0]?.s === section;

						const isActive =
							isBestMatch || section.className?.includes('bg-bg-primary');

						return (
							<li key={section.name}>
								<Link
									href={section.href ?? '#'}
									className={clsx('flex flex-col items-center p-2', {
										'pointer-events-none': section.disabled,
									})}
								>
									<Icon
										src={section.icon || ''}
										width={20}
										height={20}
										className={clsx({
											'text-primary': isActive,
											'text-opacity-black': !isActive,
										})}
									/>
									<Typography
										type="regular-10"
										className={clsx('mt-0.5 text-center', {
											'text-primary font-semibold': isActive,
											'text-opacity-black': !isActive,
										})}
									>
										{section.label}
									</Typography>
								</Link>
							</li>
						);
					})}
				</ul>
			</div>
		</>
	);
};
